import {
  GENERATED_API_PRODUCTS,
  GENERATED_PREMIUM_ADDONS,
  GENERATED_SECTOR_TIERS,
} from './pricing-registry.generated';

export type CurrencyCode = 'AUD';
export type BillingInterval = 'month' | 'year' | 'one_time' | 'per_call';
export type ProductKind = 'platform_plan' | 'sector_plan' | 'api_call' | 'premium_addon';
export type RestrictionClassification = 'public' | 'internal' | 'confidential' | 'restricted';
export type PlatformTierId = 'sentinel' | 'sovereign' | 'commander';
export type PlatformPlanTier =
  | 'FREE'
  | 'STARTER'
  | 'PRO'
  | 'TEAM'
  | 'BUSINESS'
  | 'ENTERPRISE'
  | 'ENTERPRISE_PLUS'
  | 'GOVERNMENT';

export interface BaseCanonicalProductSku {
  readonly skuId: string;
  readonly name: string;
  readonly description: string;
  readonly productKind: ProductKind;
  readonly currency: CurrencyCode;
  readonly taxRateBps: number;
  readonly billingInterval: BillingInterval;
  readonly unitAmountMinor: number | null;
  readonly entitlementFeatures: readonly string[];
  readonly usageUnit: string;
  readonly effectiveFrom: string;
  readonly effectiveTo: string | null;
  readonly selfService: boolean;
  readonly businessId?: string;
}

export interface PlatformPlanSku extends BaseCanonicalProductSku {
  readonly productKind: 'platform_plan';
  readonly planTier: PlatformPlanTier;
  readonly monthlyAmountMinor: number | null;
  readonly yearlyAmountMinor: number | null;
  readonly maxProjects: number;
  readonly maxScansPerMonth: number;
  readonly maxTeamMembers: number;
  readonly maxAIQueries: number;
}

export interface ApiProductItem extends BaseCanonicalProductSku {
  readonly productKind: 'api_call';
  readonly endpointPath: string;
  readonly restrictionClassification: RestrictionClassification;
  readonly sourceBusinessId: string;
}

export interface SectorPlanSku extends BaseCanonicalProductSku {
  readonly productKind: 'sector_plan';
  readonly sourceBusinessId: string;
  readonly callToAction: string;
}

export interface PremiumAddonSku extends BaseCanonicalProductSku {
  readonly productKind: 'premium_addon';
  readonly addonId: string;
  readonly monthlyAmountMinor: number;
  readonly yearlyAmountMinor: number;
  readonly category: string;
  readonly minimumTier: string;
}

export type CanonicalProductSku =
  | PlatformPlanSku
  | ApiProductItem
  | SectorPlanSku
  | PremiumAddonSku;

const EFFECTIVE_FROM = '2026-10-08';
const GST_RATE_BPS = 1000;
const PAID_PLATFORM_ENTITLEMENTS = [
  'platform_security_tools',
  'platform_advanced',
  'platform_media',
] as const;

function platformPlan(options: Omit<PlatformPlanSku, 'currency' | 'taxRateBps' | 'effectiveFrom' | 'effectiveTo' | 'productKind' | 'usageUnit' | 'billingInterval'>): PlatformPlanSku {
  return Object.freeze({
    ...options,
    productKind: 'platform_plan',
    currency: 'AUD',
    taxRateBps: GST_RATE_BPS,
    billingInterval: 'month',
    usageUnit: 'subscription',
    effectiveFrom: EFFECTIVE_FROM,
    effectiveTo: null,
  });
}

export const PLATFORM_PLAN_REGISTRY: Readonly<Record<PlatformPlanTier, PlatformPlanSku>> = Object.freeze({
  FREE: platformPlan({
    skuId: 'sku_platform_citizen_2026',
    name: 'Citizen',
    description: 'Promotional evaluation access. Disabled unless an explicit promotion is active.',
    planTier: 'FREE',
    unitAmountMinor: 0,
    monthlyAmountMinor: 0,
    yearlyAmountMinor: 0,
    entitlementFeatures: [],
    selfService: false,
    maxProjects: 1,
    maxScansPerMonth: 5,
    maxTeamMembers: 1,
    maxAIQueries: 10,
  }),
  STARTER: platformPlan({
    skuId: 'sku_platform_sentinel_2026',
    name: 'Sentinel',
    description: 'Core sovereign security platform access for an individual operator.',
    planTier: 'STARTER',
    unitAmountMinor: 99_000,
    monthlyAmountMinor: 99_000,
    yearlyAmountMinor: 990_000,
    entitlementFeatures: PAID_PLATFORM_ENTITLEMENTS,
    selfService: true,
    maxProjects: 3,
    maxScansPerMonth: 50,
    maxTeamMembers: 1,
    maxAIQueries: 100,
  }),
  PRO: platformPlan({
    skuId: 'sku_platform_sovereign_2026',
    name: 'Sovereign',
    description: 'Advanced platform access with predictive intelligence and API access.',
    planTier: 'PRO',
    unitAmountMinor: 499_000,
    monthlyAmountMinor: 499_000,
    yearlyAmountMinor: 4_790_000,
    entitlementFeatures: PAID_PLATFORM_ENTITLEMENTS,
    selfService: true,
    maxProjects: 10,
    maxScansPerMonth: 250,
    maxTeamMembers: 3,
    maxAIQueries: 1000,
  }),
  TEAM: platformPlan({
    skuId: 'sku_platform_commander_2026',
    name: 'Commander',
    description: 'Team platform access with autonomous operations and collaboration controls.',
    planTier: 'TEAM',
    unitAmountMinor: 1_499_000,
    monthlyAmountMinor: 1_499_000,
    yearlyAmountMinor: 14_390_000,
    entitlementFeatures: PAID_PLATFORM_ENTITLEMENTS,
    selfService: true,
    maxProjects: 50,
    maxScansPerMonth: 1500,
    maxTeamMembers: 15,
    maxAIQueries: 7500,
  }),
  BUSINESS: platformPlan({
    skuId: 'sku_platform_institutional_2026',
    name: 'Institutional',
    description: 'Institutional platform access for large security and engineering teams.',
    planTier: 'BUSINESS',
    unitAmountMinor: 2_999_000,
    monthlyAmountMinor: 2_999_000,
    yearlyAmountMinor: 28_790_000,
    entitlementFeatures: PAID_PLATFORM_ENTITLEMENTS,
    selfService: true,
    maxProjects: 200,
    maxScansPerMonth: 10_000,
    maxTeamMembers: 75,
    maxAIQueries: 50_000,
  }),
  ENTERPRISE: platformPlan({
    skuId: 'sku_platform_enterprise_2026',
    name: 'Enterprise',
    description: 'Contracted enterprise deployment. Requires an approved quote.',
    planTier: 'ENTERPRISE',
    unitAmountMinor: null,
    monthlyAmountMinor: null,
    yearlyAmountMinor: 500_000_000,
    entitlementFeatures: PAID_PLATFORM_ENTITLEMENTS,
    selfService: false,
    maxProjects: -1,
    maxScansPerMonth: -1,
    maxTeamMembers: -1,
    maxAIQueries: -1,
  }),
  ENTERPRISE_PLUS: platformPlan({
    skuId: 'sku_platform_enterprise_plus_2026',
    name: 'Enterprise+',
    description: 'Contracted multi-region enterprise deployment. Requires an approved quote.',
    planTier: 'ENTERPRISE_PLUS',
    unitAmountMinor: null,
    monthlyAmountMinor: null,
    yearlyAmountMinor: 1_000_000_000,
    entitlementFeatures: PAID_PLATFORM_ENTITLEMENTS,
    selfService: false,
    maxProjects: -1,
    maxScansPerMonth: -1,
    maxTeamMembers: -1,
    maxAIQueries: -1,
  }),
  GOVERNMENT: platformPlan({
    skuId: 'sku_platform_government_2026',
    name: 'Government & Defence',
    description: 'Contracted sovereign government deployment. Requires an approved quote.',
    planTier: 'GOVERNMENT',
    unitAmountMinor: null,
    monthlyAmountMinor: null,
    yearlyAmountMinor: 2_500_000_000,
    entitlementFeatures: PAID_PLATFORM_ENTITLEMENTS,
    selfService: false,
    maxProjects: -1,
    maxScansPerMonth: -1,
    maxTeamMembers: -1,
    maxAIQueries: -1,
  }),
});

export const CANONICAL_TIERS: Readonly<Record<PlatformTierId, PlatformPlanSku>> = Object.freeze({
  sentinel: PLATFORM_PLAN_REGISTRY.STARTER,
  sovereign: PLATFORM_PLAN_REGISTRY.PRO,
  commander: PLATFORM_PLAN_REGISTRY.TEAM,
});

function classificationForBusiness(businessId: string): RestrictionClassification {
  if (['defence', 'leo', 'interpol', 'blackflight', 'sovereign-surveillance', 'kinetic'].includes(businessId)) {
    return 'restricted';
  }
  if (['finance', 'insurance', 'medical', 'legal', 'crypto', 'biotech'].includes(businessId)) {
    return 'confidential';
  }
  return 'public';
}

export const API_PRODUCT_REGISTRY: Readonly<Record<string, ApiProductItem>> = Object.freeze(
  Object.fromEntries(
    GENERATED_API_PRODUCTS.map((product) => [
      product.skuId,
      Object.freeze({
        skuId: product.skuId,
        name: product.name,
        description: product.description,
        productKind: 'api_call' as const,
        currency: 'AUD' as const,
        taxRateBps: GST_RATE_BPS,
        billingInterval: 'per_call' as const,
        unitAmountMinor: product.unitAmountMinor,
        entitlementFeatures: [`api_product:${product.skuId}`],
        usageUnit: 'api_call',
        effectiveFrom: EFFECTIVE_FROM,
        effectiveTo: null,
        selfService: product.unitAmountMinor !== null,
        businessId: product.businessId,
        sourceBusinessId: product.sourceBusinessId,
        endpointPath: product.endpointPath,
        restrictionClassification: classificationForBusiness(product.businessId),
      }),
    ])
  )
);

export const API_PRODUCTS_BY_ENDPOINT: Readonly<Record<string, readonly ApiProductItem[]>> = Object.freeze(
  Object.values(API_PRODUCT_REGISTRY).reduce<Record<string, ApiProductItem[]>>((index, product) => {
    (index[product.endpointPath] ??= []).push(product);
    return index;
  }, {})
);

/** A business-wide API scope must never substitute for the purchased product. */
export function hasCanonicalApiProductScope(skuId: string, scopes: unknown): boolean {
  const product = API_PRODUCT_REGISTRY[skuId];
  return !!product && Array.isArray(scopes) && scopes.length === 1 &&
    scopes[0] === product.entitlementFeatures[0];
}

export const SECTOR_PLAN_REGISTRY: Readonly<Record<string, SectorPlanSku>> = Object.freeze(
  Object.fromEntries(
    GENERATED_SECTOR_TIERS.map((tier) => [
      tier.skuId,
      Object.freeze({
        skuId: tier.skuId,
        name: `${tier.businessName} — ${tier.name}`,
        description: tier.features.join('; ') || `${tier.businessName} ${tier.name} plan`,
        productKind: 'sector_plan' as const,
        currency: 'AUD' as const,
        taxRateBps: GST_RATE_BPS,
        billingInterval: tier.billingInterval,
        unitAmountMinor: tier.unitAmountMinor,
        entitlementFeatures: [`sector_${tier.businessId}`],
        usageUnit: 'sector_subscription',
        effectiveFrom: EFFECTIVE_FROM,
        effectiveTo: null,
        selfService: tier.unitAmountMinor !== null,
        businessId: tier.businessId,
        sourceBusinessId: tier.sourceBusinessId,
        callToAction: tier.callToAction,
      }),
    ])
  )
);

export const PREMIUM_ADDON_REGISTRY: Readonly<Record<string, PremiumAddonSku>> = Object.freeze(
  Object.fromEntries(
    GENERATED_PREMIUM_ADDONS.map((addon) => [
      addon.skuId,
      Object.freeze({
        skuId: addon.skuId,
        addonId: addon.addonId,
        name: addon.name,
        description: addon.description,
        productKind: 'premium_addon' as const,
        currency: 'AUD' as const,
        taxRateBps: GST_RATE_BPS,
        billingInterval: 'month' as const,
        unitAmountMinor: addon.monthlyAmountMinor,
        monthlyAmountMinor: addon.monthlyAmountMinor,
        yearlyAmountMinor: addon.yearlyAmountMinor,
        entitlementFeatures: [`addon_${addon.addonId.replace(/-/g, '_')}`],
        usageUnit: 'addon_subscription',
        effectiveFrom: EFFECTIVE_FROM,
        effectiveTo: null,
        selfService: true,
        category: addon.category,
        minimumTier: addon.minimumTier,
      }),
    ])
  )
);

export const PREMIUM_ADDON_BY_ID: Readonly<Record<string, PremiumAddonSku>> = Object.freeze(
  Object.fromEntries(Object.values(PREMIUM_ADDON_REGISTRY).map((addon) => [addon.addonId, addon]))
);

export const ALL_PRODUCT_SKUS: Readonly<Record<string, CanonicalProductSku>> = Object.freeze({
  ...Object.fromEntries(Object.values(PLATFORM_PLAN_REGISTRY).map((plan) => [plan.skuId, plan])),
  ...SECTOR_PLAN_REGISTRY,
  ...API_PRODUCT_REGISTRY,
  ...PREMIUM_ADDON_REGISTRY,
});

export function getPlatformPlan(planTier: string): PlatformPlanSku | null {
  return PLATFORM_PLAN_REGISTRY[planTier.toUpperCase() as PlatformPlanTier] ?? null;
}

export function getSku(skuId: string): CanonicalProductSku | null {
  return ALL_PRODUCT_SKUS[skuId] ?? null;
}

export function getSectorPlans(businessId: string): SectorPlanSku[] {
  const canonicalId = businessId === 'water-utilities' ? 'water' : businessId;
  return Object.values(SECTOR_PLAN_REGISTRY).filter((plan) => plan.businessId === canonicalId);
}

export function resolveSkuAmountMinor(
  sku: CanonicalProductSku,
  billingInterval: 'month' | 'year' | 'one_time' | 'per_call'
): number | null {
  if (sku.productKind === 'platform_plan') {
    if (billingInterval === 'year') return sku.yearlyAmountMinor;
    if (billingInterval === 'month') return sku.monthlyAmountMinor;
    return null;
  }
  if (sku.productKind === 'premium_addon') {
    if (billingInterval === 'year') return sku.yearlyAmountMinor;
    if (billingInterval === 'month') return sku.monthlyAmountMinor;
    return null;
  }
  return sku.billingInterval === billingInterval ? sku.unitAmountMinor : null;
}

export class PricingCheckoutError extends Error {
  readonly code: 'unknown_sku' | 'quote_required' | 'interval_not_supported';

  constructor(code: PricingCheckoutError['code'], message: string) {
    super(message);
    this.name = 'PricingCheckoutError';
    this.code = code;
  }
}

export function resolveSelfServiceCheckout(
  skuId: string,
  billingInterval: BillingInterval
): { sku: CanonicalProductSku; unitAmountMinor: number } {
  const sku = getSku(skuId);
  if (!sku) {
    throw new PricingCheckoutError('unknown_sku', `Unknown commercial SKU: ${skuId}`);
  }
  if (!sku.selfService) {
    throw new PricingCheckoutError(
      'quote_required',
      `${sku.name} is not available for self-service checkout and requires an approved quote.`
    );
  }

  const unitAmountMinor = resolveSkuAmountMinor(sku, billingInterval);
  if (unitAmountMinor === null) {
    throw new PricingCheckoutError(
      'interval_not_supported',
      `${sku.name} is not available for ${billingInterval} billing.`
    );
  }
  if (!Number.isSafeInteger(unitAmountMinor) || unitAmountMinor < 0) {
    throw new PricingCheckoutError(
      'quote_required',
      `${sku.name} does not have an approved integer AUD checkout amount.`
    );
  }

  return { sku, unitAmountMinor };
}

if (Object.keys(API_PRODUCT_REGISTRY).length !== 132) throw new Error('Canonical API registry must contain 132 products');
if (Object.keys(SECTOR_PLAN_REGISTRY).length !== 115) throw new Error('Canonical sector registry must contain 115 tiers');
if (Object.keys(PREMIUM_ADDON_REGISTRY).length !== 25) throw new Error('Canonical add-on registry must contain 25 products');